import { handleAPIResponse, getAuthHeaders } from '@/utils/apiUtils';
import { type Action } from '@/utils/constants';
import type {
  Job,
  CreateJobRequest,
  UpdateJobRequest,
  JobStatus,
  CustomInterview,
  JobCandidatesApiResponse,
  InterviewLanguage,
  ScreeningSchema,
  ScreeningCriteria,
  ScreeningFormBatch,
  ScreeningPublishAction,
  ScreeningFormGenerateParams,
  ScreeningFormGenerateResponse,
  ScreeningFormSuggestParams,
  ScreeningFormSuggestResponse,
  EditScreeningSchemaParams,
  EditScreeningSchemaResponse,
  JobCandidateListFilters,
} from '@/types/jobs';
import { Candidate } from '@/types/candidate';

const API_BASE_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

interface JobsResponse {
  jobs: Job[];
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
    limit: number;
  };
}

interface JobResponse {
  job: Job;
}

export interface ActiveJobAppliedNewCountsItem {
  jobId: string;
  title: string;
  jobCreatedAt: string;
  newAppliedLastNDaysCount: number;
}

export interface ActiveJobsAppliedNewCountsResponse {
  days: number;
  jobs: ActiveJobAppliedNewCountsItem[];
}

interface ParseJobResponse {
  job: CreateJobRequest;
}

export const jobsApi = {
  // Get all jobs
  listJobs: async (page = 1, limit = 10): Promise<JobsResponse> => {
    const response = await fetch(`${API_BASE_URL}/jobs?page=${page}&limit=${limit}`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<JobsResponse>(response);
  },

  /**
   * Fetch active jobs that have new candidates in Applied stage in the last N days.
   * Used by the dashboard home page pipeline notifications panel.
   */
  getActiveJobsAppliedNewCounts: async (): Promise<ActiveJobsAppliedNewCountsResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/jobs/active/applied-new-counts`,
      { headers: getAuthHeaders() }
    );
    // handleAPIResponse already returns result.data, so we don't need to access .data again
    return handleAPIResponse<ActiveJobsAppliedNewCountsResponse>(response);
  },

  // Get single job
  getJob: async (id: string): Promise<JobResponse> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<JobResponse>(response);
  },

  // Create new job
  createJob: async (data: CreateJobRequest): Promise<{ job: Job }> => {
    const response = await fetch(`${API_BASE_URL}/jobs`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ job: data }),
    });

    return handleAPIResponse<{ job: Job }>(response);
  },

  // Update job
  updateJob: async ({ id, ...data }: UpdateJobRequest): Promise<Job> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ job: data }),
    });

    return handleAPIResponse<Job>(response);
  },

  listScreeningForms: async (jobId: string): Promise<ScreeningFormBatch[]> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/screening-forms`, {
      headers: getAuthHeaders(),
    });
    return handleAPIResponse<ScreeningFormBatch[]>(response);
  },

  createScreeningForm: async (
    jobId: string,
    schema: ScreeningSchema,
    publishAction: ScreeningPublishAction
  ): Promise<ScreeningFormBatch> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/screening-forms`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ schema, publishAction }),
    });
    return handleAPIResponse<ScreeningFormBatch>(response);
  },

  // updateScreeningForm / publishScreeningForm removed – each save creates a new published version

  // Delete job
  deleteJob: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleAPIResponse(response);
  },

  //update job status
  updateJobStatus: async (id: string, status: JobStatus): Promise<Job> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });

    return handleAPIResponse<Job>(response);
  },

  // Update resume screening criteria (must / preferred) and recalculate scores for up to 300 non-rejected candidates
  updateResumeScreeningCriteria: async (
    jobId: string,
    screeningCriteria: ScreeningCriteria
  ): Promise<{ job: Job; recalculatedCount: number; totalEligible: number }> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/resume-screening-criteria`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ screeningCriteria }),
    });
    const result = await handleAPIResponse<{
      data: { job: Job; recalculatedCount: number; totalEligible: number };
    }>(response);
    return result.data;
  },

  // Update job voice configuration
  updateJobVoiceConfig: async (
    id: string,
    voiceConfig: { provider: 'google' | 'elevenlabs'; voiceId: string; language: InterviewLanguage }
  ): Promise<{ job: Job }> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}/voice-config`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(voiceConfig),
    });

    return handleAPIResponse<{ job: Job }>(response);
  },

  enhanceJobDescription: async (description: string): Promise<{ jobDescription: string }> => {
    const response = await fetch(`${API_BASE_URL}/jobs/generate-job-description`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ description }),
    });

    return handleAPIResponse<{ jobDescription: string }>(response);
  },

  // Publish job to platform (dummy API for now)
  publishJobToPlatform: async (
    jobId: string,
    platform: 'linkedin' | 'naukri' | 'zoho'
  ): Promise<{ success: boolean; message: string }> => {
    // TODO: Replace with actual API endpoint when ready
    // For now, this is a placeholder that simulates the API call
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/publish`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ platform }),
    });

    return handleAPIResponse<{ success: boolean; message: string }>(response);
  },

  getJobCandidates: async (
    jobId: string,
    searchQuery?: string,
    semanticQuery?: string,
    filters?: JobCandidateListFilters
  ): Promise<JobCandidatesApiResponse> => {
    const params = new URLSearchParams();
    if (semanticQuery) {
      params.set('semanticQuery', semanticQuery);
    } else if (searchQuery) {
      params.set('search', searchQuery);
    }
    if (filters && Object.keys(filters).length > 0) {
      params.set('filters', JSON.stringify(filters));
    }

    const queryString = params.toString();
    const url = queryString
      ? `${API_BASE_URL}/jobs/${jobId}/candidates?${queryString}`
      : `${API_BASE_URL}/jobs/${jobId}/candidates`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<JobCandidatesApiResponse>(response);
  },

  updateCandidateStatus: async (
    jobId: string,
    candidateIds: string | number | Array<string | number>,
    pipelineStageId: string,
    sendRejectionEmail?: boolean
  ): Promise<{ jobCandidate?: Candidate; jobCandidates?: Candidate[] }> => {
    // Always send candidateIds as an array of numbers
    const idsArray = Array.isArray(candidateIds) ? candidateIds : [candidateIds];
    const candidateIdsPayload = idsArray.map((id) => Number(id));

    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/candidates`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        jobId,
        candidateIds: candidateIdsPayload,
        pipelineStageId,
        sendRejectionEmail: sendRejectionEmail ?? false,
      }),
    });

    return handleAPIResponse<{ jobCandidate?: Candidate; jobCandidates?: Candidate[] }>(response);
  },

  parseJobDescriptionFile: async (document: File): Promise<ParseJobResponse> => {
    const formData = new FormData();
    formData.append('document', document);

    const response = await fetch(`${API_BASE_URL}/jobs/generate-from-document?preserveDescription=true`, {
      method: 'POST',
      headers: getAuthHeaders({ addContentType: false }),
      body: formData,
    });

    return handleAPIResponse<ParseJobResponse>(response);
  },

  parseJobDescriptionText: async (jobDescription: string): Promise<ParseJobResponse> => {
    const formData = new FormData();
    formData.append('jobDescription', jobDescription);

    const response = await fetch(`${API_BASE_URL}/jobs/generate-from-document?preserveDescription=true`, {
      method: 'POST',
      headers: getAuthHeaders({ addContentType: false }),
      body: formData,
    });

    return handleAPIResponse<ParseJobResponse>(response);
  },

  // Auto-format job description (text-only)
  autoFormatJobDescription: async (jobDescription: string): Promise<{ jobDescription: string }> => {
    const response = await fetch(`${API_BASE_URL}/jobs/auto-format-description`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobDescription }),
    });
    return handleAPIResponse<{ jobDescription: string }>(response);
  },

  // Upload job description with markdown formatting (preserves original structure)
  uploadJobDescription: async (jobDescription: string): Promise<{ jobDescription: string }> => {
    const formData = new FormData();
    formData.append('jobDescription', jobDescription);

    const response = await fetch(`${API_BASE_URL}/jobs/upload-job-description`, {
      method: 'POST',
      headers: getAuthHeaders({ addContentType: false }),
      body: formData,
    });

    return handleAPIResponse<{ jobDescription: string }>(response);
  },

  // Upload job description from file with markdown formatting
  uploadJobDescriptionFile: async (document: File): Promise<{ jobDescription: string }> => {
    const formData = new FormData();
    formData.append('document', document);

    const response = await fetch(`${API_BASE_URL}/jobs/upload-job-description`, {
      method: 'POST',
      headers: getAuthHeaders({ addContentType: false }),
      body: formData,
    });

    return handleAPIResponse<{ jobDescription: string }>(response);
  },

  parseJobPrompt: async (prompt: string): Promise<ParseJobResponse> => {
    const response = await fetch(`${API_BASE_URL}/jobs/generate-from-prompt`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt }),
    });

    return handleAPIResponse<ParseJobResponse>(response);
  },

  generateScreeningQuestions: async (
    jobId: string,
    params?: { questionDump?: string }
  ): Promise<Job> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/generate-screening-questions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ questionDump: params?.questionDump || '' }),
    });
    return handleAPIResponse<Job>(response);
  },

  // Generate static screening schema (does not persist)
  generateScreeningSchema: async (
    jobId: string,
    params?: ScreeningFormGenerateParams
  ): Promise<ScreeningFormGenerateResponse> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/generate-screening`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        focusAreas: params?.focusAreas,
        additionalContext: params?.additionalContext,
        questionCount: params?.questionCount,
      }),
    });

    // Normalize both wrapped ({ data }) and unwrapped ({ schema }) responses while keeping
    // handleAPIResponse-driven error handling for consistency.
    const rawBody = await response.text();
    const parsed = rawBody ? JSON.parse(rawBody) : {};

    if (!response.ok) {
      // Reconstruct a Response so handleAPIResponse can surface toasts and errors.
      const errorResponse = new Response(rawBody, {
        status: response.status,
        statusText: response.statusText,
      });
      return handleAPIResponse<ScreeningFormGenerateResponse>(errorResponse);
    }

    const schema = parsed.schema || parsed.data?.schema || parsed.data;
    return { schema };
  },

  suggestScreeningFocusAreas: async (
    jobId: string,
    params?: ScreeningFormSuggestParams
  ): Promise<ScreeningFormSuggestResponse> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/generate-screening-focus-areas`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        questionCount: params?.questionCount,
        screeningCriteria: params?.screeningCriteria,
      }),
    });

    const rawBody = await response.text();
    const parsed = rawBody ? JSON.parse(rawBody) : {};

    if (!response.ok) {
      const errorResponse = new Response(rawBody, {
        status: response.status,
        statusText: response.statusText,
      });
      return handleAPIResponse<ScreeningFormSuggestResponse>(errorResponse);
    }

    const focusAreas = parsed.focusAreas || parsed.data?.focusAreas || [];
    return { focusAreas };
  },

  suggestScreeningFocusAreasFromContext: async (
    jobData: { title?: string; description?: string; skills?: string[] },
    params?: ScreeningFormSuggestParams
  ): Promise<ScreeningFormSuggestResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/jobs/generate-screening-focus-areas-from-context`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          job: jobData,
          questionCount: params?.questionCount,
          screeningCriteria: params?.screeningCriteria,
        }),
      }
    );

    const rawBody = await response.text();
    const parsed = rawBody ? JSON.parse(rawBody) : {};

    if (!response.ok) {
      const errorResponse = new Response(rawBody, {
        status: response.status,
        statusText: response.statusText,
      });
      return handleAPIResponse<ScreeningFormSuggestResponse>(errorResponse);
    }

    const focusAreas = parsed.focusAreas || parsed.data?.focusAreas || [];
    return { focusAreas };
  },

  // Generate screening schema from job context (for create mode, no jobId needed)
  generateScreeningSchemaFromContext: async (
    jobData: { title?: string; description?: string; skills?: string[] },
    params?: ScreeningFormGenerateParams
  ): Promise<ScreeningFormGenerateResponse> => {
    const response = await fetch(`${API_BASE_URL}/jobs/generate-screening-from-context`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        job: jobData,
        focusAreas: params?.focusAreas,
        additionalContext: params?.additionalContext,
        questionCount: params?.questionCount,
        screeningCriteria: params?.screeningCriteria,
      }),
    });

    // Normalize both wrapped ({ data }) and unwrapped ({ schema }) responses while keeping
    // handleAPIResponse-driven error handling for consistency.
    const rawBody = await response.text();
    const parsed = rawBody ? JSON.parse(rawBody) : {};

    if (!response.ok) {
      // Reconstruct a Response so handleAPIResponse can surface toasts and errors.
      const errorResponse = new Response(rawBody, {
        status: response.status,
        statusText: response.statusText,
      });
      return handleAPIResponse<ScreeningFormGenerateResponse>(errorResponse);
    }

    const schema = parsed.schema || parsed.data?.schema || parsed.data;
    return { schema };
  },

  editScreeningSchema: async (
    params: EditScreeningSchemaParams
  ): Promise<EditScreeningSchemaResponse> => {
    const response = await fetch(`${API_BASE_URL}/jobs/edit-screening-schema`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        schema: params.schema,
        prompt: params.prompt,
        messages: params.messages,
      }),
    });

    const rawBody = await response.text();
    const parsed = rawBody ? JSON.parse(rawBody) : {};

    if (!response.ok) {
      const errorResponse = new Response(rawBody, {
        status: response.status,
        statusText: response.statusText,
      });
      return handleAPIResponse<EditScreeningSchemaResponse>(errorResponse);
    }

    const schema = parsed.schema || parsed.data?.schema || parsed.data;
    return { schema };
  },

  // Generate custom interview questions
  generateCustomInterview: async (jobId: string): Promise<{ customInterview: CustomInterview }> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/generate-custom-interview`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleAPIResponse<{ customInterview: CustomInterview }>(response);
  },

  // Generate unified interview (per-category) questions
  generateUnifiedInterview: async (
    jobId: string,
    params: {
      questionConfig?: {
        screening?: number;
        technical?: number;
        behavioural?: number;
        cultural?: number;
        background?: number;
        cultural_behavioural?: number;
      };
      additionalPrompt?: string;
      backgroundFocusDescription?: string;
    }
  ): Promise<{ unifiedInterview: { questions: CustomInterview['questions']; evaluationCriteria: Array<{ name: string; weight: number }> } }> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/generate-unified-interview`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(params),
    });
    return handleAPIResponse<{ unifiedInterview: { questions: CustomInterview['questions']; evaluationCriteria: Array<{ name: string; weight: number }> } }>(response);
  },

  // Create custom interview
  createCustomInterview: async (jobId: string, customInterview: CustomInterview): Promise<Job> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/custom-interview`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ customInterview }),
    });
    return handleAPIResponse<Job>(response);
  },

  // Create unified interview
  createUnifiedInterview: async (jobId: string, unifiedInterview: CustomInterview): Promise<Job> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/unified-interview`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ unifiedInterview }),
    });
    return handleAPIResponse<Job>(response);
  },

  // Update custom interview
  updateCustomInterview: async (jobId: string, customInterview: CustomInterview): Promise<Job> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/custom-interview`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ customInterview }),
    });
    return handleAPIResponse<Job>(response);
  },

  // Update unified interview
  updateUnifiedInterview: async (jobId: string, unifiedInterview: CustomInterview): Promise<Job> => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/unified-interview`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ unifiedInterview }),
    });
    return handleAPIResponse<Job>(response);
  },

  // Update pipeline stages (add/edit/remove/reorder)
  updatePipelineStages: async (
    jobId: string,
    stages: Array<{
      id?: string;
      name: string;
      order: number;
      action: Action;
    }>
  ): Promise<{ stages: Job['pipelineStages'] } > => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/stages`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ stages }),
    });
    return handleAPIResponse<{ stages: Job['pipelineStages'] }>(response);
  },

  // Export job candidates to CSV
  exportCandidates: async (
    jobId: string,
    pipelineStageId?: string,
    jobTitle?: string
  ): Promise<{ csvData: string; filename: string }> => {
    const params = new URLSearchParams();
    if (pipelineStageId) {
      params.append('pipelineStageId', pipelineStageId);
    }

    const response = await fetch(
      `${API_BASE_URL}/jobs/${jobId}/candidates/export?${params.toString()}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (response.ok) {
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('text/csv')) {
        // Generate filename using job title
        let filename = `job-${jobId}-candidates.csv`; // Default filename
        
        if (jobTitle) {
          // Allow only alphanumerics, space, underscore, and hyphen. Important: '-' inside character class is escaped or placed at end.
          // Changed from /[^a-zA-Z0-9\s-_]/ to /[^a-zA-Z0-9\s_-]/ to avoid treating '-' as a range operator.
          const sanitizedTitle = jobTitle.replace(/[^a-zA-Z0-9\s_-]/g, '').replace(/\s+/g, '_');
          const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
          filename = `${sanitizedTitle}_Candidates_Export_${timestamp}.csv`;
        }

        const csvData = await response.text();
        return { csvData, filename };
      } else {
        throw new Error('Response is not a CSV file');
      }
    } else {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }
  },

  submitScreeningForm: async (
    jobId: string,
    candidateId: string,
    responses: Record<string, string | number | string[]>
  ): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/jobs/${jobId}/candidates/${candidateId}/screening-form`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ responses }),
      }
    );
    return handleAPIResponse<void>(response);
  },
};
